import React, { useCallback, useRef, useState } from "react";
import { useInView } from "../../lib/reveal";

/* -------------------------------------------------------------------------
   Reveal : one shared entrance. Sections differ by delay and by what moves,
   never by inventing a new entrance per section.
   ------------------------------------------------------------------------- */

export const Reveal: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "li" | "section" | "article";
}> = ({ children, delay = 0, className = "", as = "div" }) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const Tag = as as any;
  return (
    <Tag
      ref={ref}
      className={`reveal ${inView ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
};

/* -------------------------------------------------------------------------
   MaskLines : a heading whose lines rise from behind their own baseline.
   ------------------------------------------------------------------------- */

export const MaskLines: React.FC<{
  lines: React.ReactNode[];
  className?: string;
  stagger?: number;
  as?: "h1" | "h2" | "h3" | "p";
}> = ({ lines, className = "", stagger = 90, as = "h2" }) => {
  const { ref, inView } = useInView<HTMLHeadingElement>({ threshold: 0.3 });
  const Tag = as as any;
  return (
    <Tag ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className={`line-mask ${inView ? "in" : ""}`}>
          <span style={{ transitionDelay: `${i * stagger}ms` }}>{line}</span>
        </span>
      ))}
    </Tag>
  );
};

/* -------------------------------------------------------------------------
   SectionMark : the one named kicker in the system: a hairline plus a short
   label, used to open a section. Not an uppercase eyebrow on every block.
   ------------------------------------------------------------------------- */

export const SectionMark: React.FC<{ label: string; index?: string }> = ({
  label,
  index,
}) => (
  <div className="flex items-center gap-4 mb-8">
    {index && <span className="t-data text-signal text-sm">{index}</span>}
    <span className="text-fg-3 text-sm font-medium tracking-wide">{label}</span>
    <span className="h-px flex-1 bg-[var(--line)]" />
  </div>
);

/* -------------------------------------------------------------------------
   Magnetic : the element leans toward the pointer, then springs back.
   Disabled on coarse pointers and under reduced motion.
   ------------------------------------------------------------------------- */

export const Magnetic: React.FC<{
  children: React.ReactNode;
  strength?: number;
  className?: string;
}> = ({ children, strength = 0.28, className = "" }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const enabled =
    typeof window !== "undefined" &&
    window.matchMedia?.("(hover: hover) and (prefers-reduced-motion: no-preference)")
      .matches;

  const onMove = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      setOffset({
        x: (e.clientX - (r.left + r.width / 2)) * strength,
        y: (e.clientY - (r.top + r.height / 2)) * strength,
      });
    },
    [enabled, strength]
  );

  const reset = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  return (
    <span
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      className={`inline-block ${className}`}
      style={{
        transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
        transition: offset.x === 0 && offset.y === 0
          ? "transform 700ms cubic-bezier(0.16,1,0.3,1)"
          : "transform 120ms cubic-bezier(0.25,1,0.5,1)",
      }}
    >
      {children}
    </span>
  );
};

/* -------------------------------------------------------------------------
   LiveDot : the only pulsing element on the site, and it means one thing:
   a person is reachable right now.
   ------------------------------------------------------------------------- */

export const LiveDot: React.FC<{ className?: string }> = ({ className = "" }) => (
  <span className={`relative flex h-1.5 w-1.5 ${className}`} aria-hidden="true">
    <span className="absolute inline-flex h-full w-full rounded-full bg-signal opacity-60 animate-blink" />
    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
  </span>
);
